import useHasAccess from "@hooks/useHasAccess"
import useIsDesktop from "@hooks/useIsDesktop"
import useSlugExtractor from "@hooks/useSlugExtractor"

import { AccessCheck } from "@particles"

import { NavTopBar, ObjectsView, ViewBar, ViewMenu } from "@organisms"
import CardsView from "@organisms/ObjectsView/CardsView/CardsView"
import TableView from "@organisms/ObjectsView/TableView/TableView"
import ViewTypesProvider from "@organisms/ObjectsView/ViewTypesProvider/ViewTypesProvider"

import { AppFrame } from "@templates"

import { CLIENTS_ROUTES } from "@routes/clients"

import { objectMap } from "@constants/objectMap"
import { PERMISSION_LEVEL } from "@constants/permissionLevel"
import {
    CLIENTS_LIST_CONTROLS_ACTIVE_TAB,
    CLIENTS_LIST_CONTROLS_KEYWORD_SEARCH,
    CLIENTS_LIST_CONTROLS_PHASE,
    CLIENTS_LIST_CONTROLS_VIEW,
} from "@constants/storage"

import { CLIENT_CARDS_VIEW_ATTRIBUTE_OPTIONS, CLIENT_CARDS_VIEW_SORT_OPTIONS } from "./ClientList.constants"
import { ClientObjectsViewTabs } from "./ClientObjectsViewTabs"
import ClientCardsView from "./views/ClientCardsView/ClientCardsView"
import ClientTableView from "./views/ClientTableView/ClientTableView"

export default function ClientList() {
    const isDesktop = useIsDesktop()
    const { hasPermission } = useHasAccess()
    const [createRoute] = useSlugExtractor([CLIENTS_ROUTES.CREATE])

    return (
        <AppFrame.Root collaborationRoomName="client-list">
            <ViewTypesProvider
                viewTypes={["table", "cards"]}
                viewTypeKey={CLIENTS_LIST_CONTROLS_VIEW}
                defaultViewType={isDesktop ? "table" : "cards"}
            >
                {({ activeViewType }) => (
                    <>
                        <NavTopBar.Root>
                            <NavTopBar.Header>
                                <NavTopBar.NavTopBarHeaderStart>
                                    <NavTopBar.PageTitle
                                        label={objectMap.client.labelPlural}
                                        icon={objectMap.client.iconActive}
                                    />
                                </NavTopBar.NavTopBarHeaderStart>
                                <NavTopBar.NavTopBarHeaderEnd>
                                    <NavTopBar.PagePresence />
                                    <AccessCheck
                                        permissions={{
                                            clients_list_permission: PERMISSION_LEVEL.RESTRICTED,
                                        }}
                                    >
                                        <AccessCheck
                                            permissions={{
                                                clients_view_permission: PERMISSION_LEVEL.FULL,
                                            }}
                                        >
                                            <NavTopBar.SearchButton scope="Clients" />
                                        </AccessCheck>
                                    </AccessCheck>
                                    <AccessCheck
                                        permissions={{
                                            clients_create_permission: PERMISSION_LEVEL.RESTRICTED,
                                        }}
                                    >
                                        <NavTopBar.CTAButton destination={createRoute} />
                                    </AccessCheck>
                                </NavTopBar.NavTopBarHeaderEnd>
                            </NavTopBar.Header>
                        </NavTopBar.Root>
                        <ObjectsView.Provider
                            activePhaseStorageKey={CLIENTS_LIST_CONTROLS_PHASE}
                            activeTabStorageKey={CLIENTS_LIST_CONTROLS_ACTIVE_TAB}
                            searchKeywordsStorageKey={CLIENTS_LIST_CONTROLS_KEYWORD_SEARCH}
                            listTitle="Clients"
                            objectType="client"
                            objectCreateRoute={createRoute}
                            objectIndexName="Client"
                            objectName="Client"
                            defaultActivePhase="active"
                            isRowClickable={hasPermission("clients_view_permission", PERMISSION_LEVEL.FULL)}
                            isEmptyStateCreateButtonVisible={hasPermission(
                                "clients_create_permission",
                                PERMISSION_LEVEL.FULL,
                            )}
                        >
                            <TableView.Provider>
                                <CardsView.Provider
                                    attributes={CLIENT_CARDS_VIEW_ATTRIBUTE_OPTIONS}
                                    sortOptions={CLIENT_CARDS_VIEW_SORT_OPTIONS}
                                >
                                    <ViewBar.Root>
                                        <ViewBar.Start>
                                            <ViewBar.Phases>
                                                <ObjectsView.Phase phase="active" title="Active" />
                                                <ObjectsView.Phase phase="inactive" title="Inactive" />
                                            </ViewBar.Phases>
                                            <ViewBar.VerticalDivider />
                                            <ClientObjectsViewTabs />
                                        </ViewBar.Start>
                                        <ViewBar.End>
                                            <ObjectsView.SearchInput />
                                            <ViewMenu />
                                        </ViewBar.End>
                                    </ViewBar.Root>
                                    {activeViewType === "table" && <ClientTableView />}
                                    {activeViewType === "cards" && <ClientCardsView />}
                                </CardsView.Provider>
                            </TableView.Provider>
                        </ObjectsView.Provider>
                    </>
                )}
            </ViewTypesProvider>
        </AppFrame.Root>
    )
}
